import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import CierreCajaModal from './finanzas/CierreCajaModal';
import RendicionesBlock from './dashboard/RendicionesBlock';
import BackupIndicador from './dashboard/BackupIndicador';
import RecorridoHoy from './dashboard/RecorridoHoy';
import ResolverHoy from './dashboard/ResolverHoy';
import FichaVisitaSheet from './dashboard/FichaVisitaSheet';
import { PAGINA } from './ui/Pantalla';
import { Seccion } from './dashboard/PanelBloques';
import { M } from './servicio/ServicioUI';
import MiEspacioChecklist from './miespacio/MiEspacioChecklist';
import { useMiEspacio } from './miespacio/useMiEspacio';
import { calcTotal } from './dashboard/estadoConstants';
import { LuWrench, LuShoppingCart, LuUserPlus, LuChevronRight } from 'react-icons/lu';
import { getTodayISO, formatDateISO } from '../utils/dateUtils';
import { visitaAtrasada } from './trabajos/gruposTrabajos';

// Inicio — 27-oct-2026: es el día de hoy, completo. Trabajos queda para lo que no es hoy (agendar, lo que
// viene, cobrar). De arriba hacia abajo: saludo · Crear (a mano, sin scrollear) · Para resolver hoy ·
// Mis tareas · Recorrido de hoy por técnico · Rendiciones · Plata de hoy · backup.
// La agenda por franjas pasó a Trabajos → Agenda (junto con la Semana de zonas).

const diasDesde = (f) => Math.floor((Date.now() - new Date(String(f).slice(0, 10) + 'T00:00:00').getTime()) / 86400000);

export default function DashboardCaja({ setVistaActual }) {
    const [ficha, setFicha] = useState(null); // visita abierta desde la agenda
    const { esAdmin, usuario } = useAuth();
    const [modalCierre, setModalCierre] = useState(false);
    // Herramientas por N/S (clientes con tarifa mensual) — vivían en "Hoy" del técnico (5-oct-2026)
    const [cargando, setCargando] = useState(true);
    const [servicios, setServicios] = useState([]);
    const [ordenes, setOrdenes] = useState([]);
    const [alertasRadar, setAlertasRadar] = useState([]);
    const [stockBajo, setStockBajo] = useState(0);
    const [cobrados, setCobrados] = useState([]);
    const [semana, setSemana] = useState(null);   // pedidos de empresa sin agendar (con urgentes)
    const miEspacio = useMiEspacio();

    const cargar = useCallback(async () => {
        setCargando(true);
        try {
            const enDosMeses = new Date(); enDosMeses.setDate(enDosMeses.getDate() + 60);
            const [sRes, oRes, rRes, pRes, cRes, wRes] = await Promise.all([
                api.get('/servicios', { params: { estado: 'PRESUPUESTO,APROBADO,EN_PROGRESO,COMPLETADO,PENDIENTE_FACTURACION,FACTURADO', page: 0, size: 500, sort: 'fechaServicio,desc' } }),
                esAdmin ? api.get('/ordenes', { params: { desde: '2020-01-01', hasta: formatDateISO(enDosMeses) } }) : Promise.resolve({ data: [] }),
                esAdmin ? api.get('/radar/alertas').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
                esAdmin ? api.get('/repuestos?page=0&size=500').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
                esAdmin ? api.get('/servicios', { params: { estado: 'COBRADO', page: 0, size: 200, sort: 'fechaServicio,desc' } }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
                esAdmin ? api.get('/mapa/semana').catch(() => ({ data: null })) : Promise.resolve({ data: null }),
            ]);
            setCobrados(cRes.data?.content || cRes.data || []);
            setSemana(wRes.data || null);
            setServicios(sRes.data?.content || sRes.data || []);
            setOrdenes(Array.isArray(oRes.data) ? oRes.data : []);
            setAlertasRadar(rRes.data || []);
            const reps = pRes.data?.content || pRes.data || [];
            setStockBajo(reps.filter(r => r.stock != null && Number(r.stock) <= 3).length);
        } catch (err) { console.warn('Panel: error cargando datos', err); } finally { setCargando(false); }
    }, [esAdmin]);

    useEffect(() => { cargar(); }, [cargar]);

    const hoy = getTodayISO();

    // Para resolver hoy (27-oct-2026): cada número abre Trabajos ya filtrado
    const resolver = useMemo(() => {
        // Trabajos lee el filtro de sessionStorage al abrirse (igual que "Cobrar" desde Clientes)
        const ir = (filtro) => () => {
            try { sessionStorage.setItem('trabajosAbrir', JSON.stringify({ filtro })); } catch { /* sin storage: abre la lista */ }
            setVistaActual('trabajos');
        };
        const pedidos = semana ? [...(semana.sinDia || []), ...(semana.dias || []).flatMap(d => d.reservados || [])] : [];
        const urgentes = pedidos.filter(p => p.urgente);
        const deHoyOManiana = (o) => { const f = String(o.fechaProgramada || '').slice(0, 10); const m = new Date(); m.setDate(m.getDate() + 1);
            return f === hoy || f === formatDateISO(m); };
        const sinOk = ordenes.filter(o => o.estado === 'PENDIENTE' && !o.confirmadaEn && !o.sinConfirmar && !o.horarioACoordinar && !o.noPuedeEn && deHoyOManiana(o));
        const noPuede = ordenes.filter(o => o.estado === 'PENDIENTE' && o.noPuedeEn);
        // Mismo criterio que la lista de Trabajos (28-oct-2026: acá contaba 5 y allá no aparecía ninguna)
        const atrasadas = ordenes.filter(o => visitaAtrasada(o, hoy));
        const nombre = (l, f) => (l.length === 1 ? f(l[0]) : null);
        const s = (n, a, b = 's') => (n === 1 ? a : a + b);
        const items = [];
        // Pedidos sin agendar en una sola tarjeta (28-oct-2026): antes "urgentes" y "pedidos" eran dos y
        // las dos abrían casi la misma lista. Roja si hay alguno urgente, y lo nombra.
        if (pedidos.length) items.push({ id: 'ped', tono: urgentes.length ? 'rojo' : 'gris', n: pedidos.length, onClick: ir('por-agendar'),
            texto: `${s(pedidos.length, 'pedido')} sin agendar`,
            detalle: urgentes.length ? `${urgentes.length === 1 ? `Urgente: ${urgentes[0].nombre}` : `${urgentes.length} urgentes`}` : null });
        if (sinOk.length) items.push({ id: 'ok', tono: 'ambar', n: sinOk.length, onClick: ir('sin-ok'),
            texto: `${s(sinOk.length, 'visita')} sin "ok, voy"`, detalle: nombre(sinOk, o => o.lugar || o.clienteNombre) });
        if (noPuede.length) items.push({ id: 'np', tono: 'ambar', n: noPuede.length, onClick: ir('no-puede'),
            texto: `${noPuede.length === 1 ? 'técnico no puede' : 'técnicos no pueden'} ir`, detalle: nombre(noPuede, o => o.lugar || o.clienteNombre) });
        if (atrasadas.length) items.push({ id: 'atr', tono: 'rojo', n: atrasadas.length, onClick: ir('atrasadas'),
            texto: `${s(atrasadas.length, 'visita')} ${s(atrasadas.length, 'atrasada')}`, detalle: nombre(atrasadas, o => o.lugar || o.clienteNombre) });
        // Zona sugerida (27-oct-2026): abre la Agenda de Trabajos en ese día
        (semana?.sugerenciasZona || []).slice(0, 1).forEach(z => items.push({ id: `zona${z.fecha}`, tono: 'azul', n: (semana.sugerenciasZona || []).length, ancho: true,
            texto: `${(semana.sugerenciasZona || []).length === 1 ? 'cambio de zona sugerido' : 'cambios de zona sugeridos'} · ${z.dia.split(' ')[0]} a ${z.haciaNombre}`,
            onClick: () => {
                try { sessionStorage.setItem('trabajosAbrir', JSON.stringify({ vista: 'agenda', fecha: String(z.fecha).slice(0, 10) })); } catch { /* */ }
                setVistaActual('trabajos');
            } }));
        // Lo que no apura, en una línea
        const extras = [];
        const sinRespuesta = servicios.filter(x => x.estado === 'PRESUPUESTO' && !x.enEspera && x.fecha && diasDesde(x.fecha) > 7);
        if (sinRespuesta.length) extras.push({ id: 'ppto', onClick: ir('presupuestos'), texto: `${sinRespuesta.length} ${s(sinRespuesta.length, 'presupuesto')} sin respuesta` });
        const paraOfrecer = new Set(alertasRadar.filter(a => !a.ofrecidoReciente).map(a => a.clienteId)).size;
        if (paraOfrecer) extras.push({ id: 'radar', onClick: () => setVistaActual('radar'), texto: `${paraOfrecer} ${paraOfrecer === 1 ? 'cliente' : 'clientes'} para ofrecer mantenimiento` });
        if (stockBajo) extras.push({ id: 'stock', onClick: () => setVistaActual('productos'), texto: `${stockBajo} con stock bajo` });
        return { items, extras };
    }, [ordenes, servicios, alertasRadar, stockBajo, hoy, setVistaActual, semana]);

    const deHoy = useMemo(() => {
        const visitas = ordenes.filter(o => String(o.fechaProgramada || '').slice(0, 10) === hoy && ['PENDIENTE', 'EN_CAMINO', 'EN_SITIO', 'COMPLETADA', 'NO_ATENDIDO'].includes(o.estado));
        const cob = cobrados.filter(x => String(x.fechaCobro || '').slice(0, 10) === hoy);
        return { visitas: visitas.length, hechas: visitas.filter(o => o.estado === 'COMPLETADA').length,
            cobradoN: cob.length, cobrado: cob.reduce((a, x) => a + (Number(x.montoFinal) > 0 ? Number(x.montoFinal) : calcTotal(x)), 0) };
    }, [ordenes, cobrados, hoy]);

    const nombre = (usuario?.nombre || '').split(' ')[0];
    const fechaLarga = new Date().toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });
    const rapido = 'h-12 rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.08] flex items-center justify-center gap-1.5 text-body font-black text-ink active:scale-95';

    const pull = usePullToRefresh(() => cargar());

    return (
        <div className={PAGINA} {...pull.handlers}>
            {pull.indicador}
            <div className="max-w-6xl mx-auto px-4 md:px-6 pt-4 md:pt-6 space-y-5">

                {/* 1. Saludo + cómo viene el día */}
                <div className="flex justify-between items-start">
                    <div>
                        <h2 className="text-2xl font-black tracking-tight text-ink">Hola{nombre ? `, ${nombre}` : ''}</h2>
                        <p className="text-caption text-muted first-letter:uppercase">
                            {fechaLarga}{esAdmin && deHoy.visitas ? ` · ${deHoy.visitas} visita${deHoy.visitas !== 1 ? 's' : ''} · ${deHoy.hechas} hecha${deHoy.hechas !== 1 ? 's' : ''}` : ''}
                        </p>
                    </div>
                    <button onClick={cargar} disabled={cargando}
                        aria-label="Recargar" className="w-11 h-11 rounded-xl flex items-center justify-center active:scale-95 disabled:opacity-40 text-muted">
                        <span className={`text-sm ${cargando ? 'animate-spin' : ''}`}>↻</span>
                    </button>
                </div>

                {/* 2. Crear: arriba, en la zona del pulgar sin scrollear (Lucas, 27-oct-2026) */}
                {esAdmin && (
                    <div className="grid grid-cols-3 gap-2 -mt-1">
                        <button type="button" onClick={() => setVistaActual('trabajos', { crear: 'nuevo' })} className={rapido}>
                            <LuWrench size={16} className="text-brand-red" /> Trabajo
                        </button>
                        <button type="button" onClick={() => setVistaActual('trabajos', { crear: 'venta' })} className={rapido}>
                            <LuShoppingCart size={16} className="text-brand-red" /> Venta
                        </button>
                        <button type="button" onClick={() => setVistaActual('clientes', { crear: true })} className={rapido}>
                            <LuUserPlus size={16} className="text-brand-red" /> Cliente
                        </button>
                    </div>
                )}

                {/* 3. Para resolver hoy */}
                {esAdmin && (
                    <Seccion titulo="Para resolver hoy">
                        {cargando && !ordenes.length ? <div className="h-20 rounded-2xl bg-card animate-pulse" /> : <ResolverHoy {...resolver} />}
                    </Seccion>
                )}

                {/* 4. Mis tareas, debajo de lo que hay que resolver (Lucas, 27-oct-2026) */}
                <Seccion titulo="Mis tareas" link="Mi espacio" onLink={() => setVistaActual('mi-espacio')}>
                    <div className="rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] p-3.5">
                        <MiEspacioChecklist espacio={miEspacio.espacio} actualizar={miEspacio.actualizar} cargando={miEspacio.cargando} />
                    </div>
                </Seccion>

                {/* 5. Recorrido de hoy: cada visita de hoy está acá y en ningún otro lado */}
                {esAdmin && (
                    <Seccion titulo="Recorrido de hoy" link="Ver el mapa" onLink={() => setVistaActual('mapa')}>
                        <RecorridoHoy ordenes={ordenes} cargando={cargando} onAbrir={setFicha} onCambio={cargar}
                            onArmar={() => setVistaActual('trabajos', { crear: 'armar' })} />
                    </Seccion>
                )}

                {/* 6. Rendiciones (solo aparece si hay algo para recibir) */}
                {esAdmin && <RendicionesBlock card="rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06]" />}

                {/* 7. Plata de hoy. Lo que falta cobrar está en Trabajos → Por cobrar */}
                {esAdmin && (
                    <Seccion titulo="Plata de hoy" link="Finanzas" onLink={() => setVistaActual('finanzas')}>
                        <div className="rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] overflow-hidden">
                            <div className="flex items-center justify-between gap-3 px-3.5 py-3 border-b border-black/[0.06] dark:border-white/[0.06]">
                                <span className="text-body font-bold text-ink">Cobrado hoy{deHoy.cobradoN ? <span className="text-caption text-muted font-bold"> · {deHoy.cobradoN} trabajo{deHoy.cobradoN !== 1 ? 's' : ''}</span> : null}</span>
                                <M valor={deHoy.cobrado} className="text-body-lg font-black text-[var(--etapa-listo-tx)]" />
                            </div>
                            <button type="button" onClick={() => setModalCierre(true)} className="w-full flex items-center justify-between gap-3 px-3.5 py-3 text-left active:bg-chip">
                                <span className="text-body font-bold text-ink">Cierre de caja</span>
                                <span className="flex items-center gap-1 text-caption text-muted">Hacer cierre <LuChevronRight size={16} /></span>
                            </button>
                        </div>
                    </Seccion>
                )}

                {/* 8. Sistema */}
                {esAdmin && <BackupIndicador />}
            </div>

            {ficha && (
                <FichaVisitaSheet key={ficha.id} orden={ficha} onCerrar={() => setFicha(null)}
                    onVerTrabajos={() => { setFicha(null); setVistaActual('trabajos'); }}
                    onEliminada={() => { setFicha(null); cargar(); }} onCambio={cargar} />
            )}
            {modalCierre && (
                <CierreCajaModal
                    onClose={() => setModalCierre(false)}
                    onArchivar={() => { setModalCierre(false); cargar(); }}
                />
            )}
        </div>
    );
}
